import { createApp } from "vue"
import { createPinia } from "pinia"
import piniaPersistedState from "pinia-plugin-persistedstate"
import { Quasar } from "quasar"
import axios from "axios"

import App from "@/App.vue"
import router from "@/router.js"
import quasarUserOptions from "@/quasarUserOptions.js"
import { useSessionStore } from "@/stores/userSession.js"

import "@/global.scss"

const pinia = createPinia()
pinia.use(piniaPersistedState)

const app = createApp(App)

app.use(pinia)
app.use(router)
app.use(Quasar, quasarUserOptions)

axios.interceptors.request.use((config) => {
  const sessionStore = useSessionStore()
  if (sessionStore.session?.token) {
    config.headers.Authorization = `Bearer ${sessionStore.session.token}`
  }
  return config
})

// Refreshing and signing out must never be retried through this: a failed refresh would ask for
// another refresh, and round it would go.
const isSessionCall = (url) => typeof url === "string" && /\/api\/webUsers\/(refresh|logout|authenticate)$/.test(url)

// Until now nothing looked at response status, so once a token was dead every call quietly failed
// and screens rendered empty instead of returning to the login page.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config
    const sessionStore = useSessionStore()

    const cannotRetry =
      error?.response?.status !== 401 ||
      config == null ||
      config.sessionRetry === true ||
      isSessionCall(config.url) ||
      sessionStore.session == null

    if (cannotRetry) return Promise.reject(error)

    // Marked before retrying, so a request that 401s again is rejected rather than looping.
    config.sessionRetry = true

    try {
      await sessionStore.refreshToken()
    } catch {
      // Only a refused refresh ends the session. Timeouts and 5xx leave it alone, which is what
      // used to sign people out over a momentary blip.
      await sessionStore.logOut()
      router.push({ name: "login" })
      return Promise.reject(error)
    }

    // The request interceptor puts the new token on this attempt.
    return axios(config)
  }
)

// The persistence plugin does not watch storage events, so each tab kept its own copy of the
// session and the second one to refresh presented a token the first had already spent. Adopting
// what the other tab wrote is what stops two open tabs signing each other out.
window.addEventListener("storage", (event) => {
  if (event.key !== "userSession") return
  useSessionStore().adoptPersistedSession()
})

app.mount("#app")
